import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE, ROUTES, NOINDEX_EXACT } from "../seoData";

// Set REACT_APP_SITE_URL in your .env (e.g. https://www.yourdomain.com).
export { SITE_URL };

const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
const ROBOTS_NOINDEX = "noindex, nofollow, noarchive";

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const removeMeta = (attr, key) => {
  document.head.querySelectorAll(`meta[${attr}="${key}"]`).forEach((el) => el.remove());
};

const upsertLink = (rel, href, extra = {}) => {
  const hreflang = extra.hreflang;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const removeLink = (rel) => {
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.remove());
};

const toAbsolute = (value) => (value.startsWith("http") ? value : `${SITE_URL}${value}`);

/** Normalise a route path so "/about-us/" and "/about-us" produce the same canonical. */
const cleanPath = (path) => {
  if (!path || path === "/") return "/";
  return path.replace(/\/+$/, "");
};

/** Builds a BreadcrumbList for any indexable route: Home > (Solutions >) Page */
export const buildBreadcrumb = (path) => {
  const p = cleanPath(path);
  const route = ROUTES[p];
  if (!route || p === "/") return null;
  const items = [{ name: "Home", item: SITE_URL }];
  if (route.group === "solution") items.push({ name: "Solutions", item: null });
  items.push({ name: route.name, item: `${SITE_URL}${p}` });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}${p}#breadcrumb-auto`,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      ...(it.item ? { item: it.item } : {}),
    })),
  };
};

const hasType = (list, type) => list.some((o) => o && (o["@type"] === type || (Array.isArray(o["@type"]) && o["@type"].includes(type))));

/**
 * Lightweight head manager (no extra dependency).
 *
 * Props:
 *  title, description  – default to ROUTES[path] when omitted
 *  path                – route path, used for canonical / og:url
 *  image, imageAlt     – social share image (1200x630 recommended)
 *  jsonLd              – object | array of schema.org objects
 *  noindex             – true to keep a page out of search results
 *  type                – og:type (default "website")
 */
const Seo = ({
  title,
  description,
  path = "/",
  image = SITE.ogImage,
  imageAlt,
  jsonLd,
  noindex = false,
  type = "website",
}) => {
  const cleaned = cleanPath(path);
  const route = ROUTES[cleaned] || {};
  const finalTitle = title || route.title || SITE.name;
  const finalDescription = description || route.description || "";
  const url = cleaned === "/" ? `${SITE_URL}/` : `${SITE_URL}${cleaned}`;
  const imageUrl = toAbsolute(image);
  const alt = imageAlt || `${SITE.name} — ${route.name || "AI lead management system & CRM"}`;

  const list = Array.isArray(jsonLd) ? jsonLd.filter(Boolean) : jsonLd ? [jsonLd] : [];
  const breadcrumb = hasType(list, "BreadcrumbList") ? null : buildBreadcrumb(cleaned);
  const ld = JSON.stringify(breadcrumb ? [...list, breadcrumb] : list);

  useEffect(() => {
    document.title = finalTitle;
    document.documentElement.lang = "en-IN";

    upsertMeta("name", "description", finalDescription);
    upsertMeta("name", "robots", noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX);
    upsertMeta("name", "googlebot", noindex ? ROBOTS_NOINDEX : ROBOTS_INDEX);
    upsertMeta("name", "theme-color", "#48247f");

    if (noindex) {
      removeLink("canonical");
      document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
    } else {
      upsertLink("canonical", url);
      upsertLink("alternate", url, { hreflang: "en-IN" });
      upsertLink("alternate", url, { hreflang: "x-default" });
    }

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:title", finalTitle);
    upsertMeta("property", "og:description", finalDescription);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", alt);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");
    upsertMeta("property", "og:locale", "en_IN");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:site", "@lmsbots");
    upsertMeta("name", "twitter:title", finalTitle);
    upsertMeta("name", "twitter:description", finalDescription);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", alt);

    // JSON-LD structured data
    const id = "seo-jsonld";
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = ld;

    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, [finalTitle, finalDescription, url, imageUrl, alt, ld, noindex, type]);

  return null;
};

/**
 * Mount ONCE inside <BrowserRouter> (see App.js).
 * Any route that is not a public marketing page (login, token pages and the whole
 * logged-in CRM) gets `noindex, nofollow`, so private screens can never leak into
 * search results — and stale "index" tags from a previous public page are cleared.
 */
export const RouteRobotsGuard = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const p = cleanPath(pathname);
    const isPublic = Object.prototype.hasOwnProperty.call(ROUTES, p) && !NOINDEX_EXACT.includes(p);
    if (isPublic) return; // <Seo> on the page manages everything

    upsertMeta("name", "robots", ROBOTS_NOINDEX);
    upsertMeta("name", "googlebot", ROBOTS_NOINDEX);
    removeLink("canonical");
    removeMeta("property", "og:url");
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  }, [pathname]);

  return null;
};

export default Seo;
